/**
 * exportUtils.js
 *
 * Utility functions for exporting Survey Explorer data in CSV and PNG formats.
 */

import { showToast } from '../components/Toast';

/**
 * Add UTF-8 BOM for Excel compatibility
 */
const BOM = '\uFEFF';

/**
 * Serialize an array of objects to CSV and trigger browser download.
 * @param {object[]} rows
 * @param {string} filename
 */
export function exportAggregatedCsv(rows, filename = 'chart-data.csv') {
  if (!rows || !rows.length) return;
  const cols = Object.keys(rows[0]);
  const lines = [
    cols.map(escapeCell).join(','),
    ...rows.map(row => cols.map(c => escapeCell(row[c] ?? '')).join(',')),
  ];
  const csvContent = BOM + lines.join('\r\n');
  downloadBlob(csvContent, filename, 'text/csv;charset=utf-8;');
  showToast(`Downloaded ${filename}`);
}

/**
 * Serialize rows to an RFC 4180 CSV string using an EXPLICIT column list.
 *
 * Unlike exportAggregatedCsv (which derives columns from Object.keys(rows[0])
 * and therefore mis-shapes fixed-schema exports when the first row is missing a
 * field), this takes the column set as a parameter so the schema is stable
 * regardless of per-row gaps — required by the Explorer export (plan §2.3).
 *
 * @param {object[]} rows
 * @param {{header: string, get: (row: object) => any}[]} columns  ordered column defs
 * @returns {string} CSV text (CRLF-delimited, trailing CRLF). No BOM — the
 *   caller prepends one if writing a file for Excel.
 */
export function rowsToCsv(rows, columns) {
  const headerRow = columns.map((column) => escapeCell(column.header)).join(',');
  if (!rows || rows.length === 0) {
    return `${headerRow}\r\n`;
  }
  let csvString = `${headerRow}\r\n`;
  for (const row of rows) {
    csvString += columns.map((column) => escapeCell(column.get(row))).join(',') + '\r\n';
  }
  return csvString;
}

/**
 * Build an Extended M3U playlist string (plan §2.2). Paths are written verbatim
 * and are expected to be RELATIVE to the playlist's own location, so moving the
 * exported folder keeps every reference resolvable.
 *
 * Core generated by the local Gemma3 worker, reviewed here (fixed a missing
 * export and a `0 || -1` duration bug).
 *
 * @param {{path: string, durationSeconds?: number|null, title: string}[]} entries
 * @returns {string} M3U text ("\n"-delimited, trailing newline). Empty → "#EXTM3U\n".
 */
export function buildM3U(entries) {
  let out = '#EXTM3U\n';
  for (const entry of entries || []) {
    const dur = Number.isFinite(entry.durationSeconds) ? Math.round(entry.durationSeconds) : -1;
    out += `#EXTINF:${dur},${entry.title}\n${entry.path}\n`;
  }
  return out;
}

/**
 * Export raw responses as CSV.
 * @param {object[]} responses
 * @param {{ [varName]: { canonicalGroups, valueAssignments } }} mapping
 * @param {{ id, name, tagIds }[]} cohorts
 * @param {string} filename
 */
export function exportRawCsv(responses, mapping = {}, cohorts = [], filename = 'raw-responses.csv') {
  if (!responses || !responses.length) return;

  const processedRows = responses.map(r => {
    // Add cohort assignment (comma-separated if in multiple)
    const matchingCohorts = cohorts
      .filter(c => (r.recipient_tags || []).some(t => c.tagIds.includes(t)))
      .map(c => c.name);
    const cohortLabel = matchingCohorts.join(', ') || '';

    // Add mapped canonical values for each mapped variable
    const mappedFields = {};
    Object.entries(mapping).forEach(([varName, m]) => {
      const rawVal = r[varName];
      const groupId = m.valueAssignments?.[rawVal];
      const group = m.canonicalGroups?.find(g => g.id === groupId);
      mappedFields[`${varName}_mapped`] = group ? group.name : '';
    });

    return {
      ...r,
      recipient_group: r.recipient_group || '',
      cohorts: cohortLabel,
      ...mappedFields,
    };
  });

  exportAggregatedCsv(processedRows, filename);
}

/**
 * Capture a DOM element as PNG and trigger download.
 * @param {React.RefObject} containerRef
 * @param {string} filename
 */
export async function exportChartPng(containerRef, filename = 'chart.png') {
  if (!containerRef?.current) return;
  try {
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(containerRef.current, {
      scale: 2, // retina quality
      backgroundColor: '#ffffff', // match light theme background
      useCORS: true,
    });
    canvas.toBlob(blob => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = filename;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      showToast(`Chart saved as ${filename}`);
    }, 'image/png');
  } catch (err) {
    console.error('PNG export failed:', err);
    showToast('PNG export failed', 'error');
  }
}

/**
 * Download rows as CSV using an EXPLICIT ordered column list (stable schema even
 * when some rows lack a field). Prepends a UTF-8 BOM for Excel.
 * @param {object[]} rows
 * @param {{header: string, get: (row: object) => any}[]} columns
 * @param {string} filename
 */
export function exportRowsCsv(rows, columns, filename = 'export.csv') {
  const csv = BOM + rowsToCsv(rows, columns);
  downloadBlob(csv, filename, 'text/csv;charset=utf-8;');
  showToast(`Downloaded ${filename}`);
}

/**
 * Export a chart + its data table as a PDF using the browser's native
 * print-to-PDF (no extra dependency). Captures the chart element as a PNG via
 * html2canvas, then opens a print-styled window with the image above an HTML
 * table and invokes print(); the user picks "Save as PDF".
 *
 * @param {object} opts
 * @param {React.RefObject} opts.containerRef  chart DOM element to capture (optional)
 * @param {string} opts.title
 * @param {{header: string, get: (row: object) => any}[]} opts.columns
 * @param {object[]} opts.rows
 */
export async function exportChartAndTablePdf({ containerRef, title = 'Export', columns = [], rows = [] }) {
  let imgDataUrl = '';
  try {
    if (containerRef?.current) {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(containerRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      imgDataUrl = canvas.toDataURL('image/png');
    }
  } catch (err) {
    console.error('Chart capture failed; exporting table only:', err);
  }

  const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const thead = '<tr>' + columns.map((c) => `<th>${esc(c.header)}</th>`).join('') + '</tr>';
  const tbody = rows.map((r) => '<tr>' + columns.map((c) => `<td>${esc(c.get(r))}</td>`).join('') + '</tr>').join('');

  const win = window.open('', '_blank');
  if (!win) { showToast('Pop-up blocked — allow pop-ups to export the PDF', 'error'); return; }
  win.document.write(
    '<!doctype html><html><head><meta charset="utf-8"><title>' + esc(title) + '</title><style>'
    + 'body{font-family:system-ui,-apple-system,sans-serif;color:#202124;margin:24px;}'
    + 'h1{font-size:18px;margin:0 0 12px;}'
    + 'img{display:block;max-width:100%;height:auto;margin-bottom:16px;border:1px solid #E8EAED;border-radius:6px;}'
    + 'table{width:100%;border-collapse:collapse;font-size:11px;}'
    + 'th,td{border:1px solid #E8EAED;padding:4px 6px;text-align:left;vertical-align:top;}'
    + 'th{background:#F8F9FA;}thead{display:table-header-group;}tr{break-inside:avoid;}'
    + '@media print{@page{size:landscape;margin:12mm;}body{margin:0;}h1{font-size:16px;margin-bottom:8mm;}img{max-height:78mm;margin-bottom:8mm;object-fit:contain;}table{font-size:8.5pt;table-layout:fixed;}th,td{padding:3px 4px;overflow-wrap:anywhere;word-break:break-word;}}'
    + '</style></head><body>'
    + '<h1>' + esc(title) + '</h1>'
    + (imgDataUrl ? '<img src="' + imgDataUrl + '" alt="chart" />' : '')
    + '<table><thead>' + thead + '</thead><tbody>' + tbody + '</tbody></table>'
    + '<script>window.onload=function(){setTimeout(function(){window.focus();window.print();},300);};</script>'
    + '</body></html>'
  );
  win.document.close();
}

// --- Internal helpers ---

function escapeCell(val) {
  const s = String(val ?? '');
  // Wrap in quotes if contains comma, newline, or quote
  if (s.includes(',') || s.includes('\n') || s.includes('"')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function downloadBlob(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = filename;
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Build a standardized export filename.
 * e.g. "my-survey__deploy-slug__bar__2024-01-15T143022.csv"
 */
export function buildFilename(surveyName, deploySlug, chartType, ext) {
  const ts = new Date().toISOString().slice(0, 19).replace(/:/g, '');
  const parts = [surveyName, deploySlug, chartType, ts].map(s => slugify(s));
  return parts.filter(Boolean).join('__') + '.' + ext;
}

function slugify(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
